import React, { useState } from 'react';
import { X, Users } from 'lucide-react';
import { useTeams } from '../hooks/useTeams';

interface JoinTeamModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export const JoinTeamModal: React.FC<JoinTeamModalProps> = ({
  isOpen,
  onClose,
  onSuccess
}) => {
  const { joinTeam, isJoining } = useTeams();
  const [joinCode, setJoinCode] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    
    if (!joinCode.trim()) {
      setError('Please enter a join code');
      return;
    }

    try {
      await joinTeam({ join_code: joinCode.trim().toUpperCase() });
      setJoinCode('');
      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('Failed to join team:', error);
      setError('Invalid or expired join code. Please check with your team owner for a new code.');
    }
  };

  const handleClose = () => {
    setJoinCode('');
    setError('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <Users className="w-6 h-6 text-blue-600" />
            <h2 className="text-xl font-semibold text-gray-900">Join Team</h2>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6">
          <div className="mb-6">
            <label htmlFor="joinCode" className="block text-sm font-medium text-gray-700 mb-2">
              Join Code
            </label>
            <input
              type="text"
              id="joinCode"
              value={joinCode}
              onChange={(e) => {
                setJoinCode(e.target.value.toUpperCase());
                setError('');
              }}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-center text-lg tracking-wider"
              placeholder="ABCD1234"
              maxLength={8}
              style={{ textTransform: 'uppercase' }}
            />
            <p className="text-xs text-gray-500 mt-2">
              Enter the 8-character code provided by your team owner
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {/* Info */}
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <h3 className="text-sm font-medium text-blue-900 mb-2">How to join a team:</h3>
            <ol className="text-sm text-blue-800 space-y-1">
              <li>1. Get a join code from your team owner</li>
              <li>2. Enter the code above</li>
              <li>3. Click "Join Team" to become a member</li>
            </ol>
            <p className="text-xs text-blue-700 mt-2">
              Note: Join codes are single-use and expire after 7 days
            </p>
          </div>

          {/* Form Actions */}
          <div className="flex gap-3">
            <button
              type="button"
              onClick={handleClose}
              className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isJoining || !joinCode.trim()}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isJoining ? 'Joining...' : 'Join Team'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}; 